/**
 * Hero stage: the real VRM hero(es), toon-shaded through the game's own HeroView (same rig
 * prep, weapons, outlines, idle pose and spring bones as in a run), standing on a simple studio
 * floor: dark slightly reflective disc, one soft spotlight pool, a faint gold ring engraving and
 * a blue rim light. The canvas is transparent so the page's navy gradient shows around it.
 *
 *   const stage = mountHeroStage(host, { heroes: ['blademaster'] });   // host sized by CSS
 *   …
 *   stage.dispose();                                                  // from the screen's dispose
 *
 * One WebGL context for the whole meta UI: every stage shares one renderer + canvas. Stages
 * stack; the newest mounted one owns the canvas, and when it is disposed the previous one gets
 * it back. Rendering pauses while the host is off-screen / hidden / the tab is hidden.
 * `releaseHeroStageGL()` frees the context (e.g. before a run when no stage is mounted).
 */
import * as THREE from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import type { UnitId } from '@keystone/sim';
import type { RenderUnit } from '../../render/types';
import { HERO_VRM, type ToonAssets, loadToonAssets } from '../../render/toon/assets';
import { HERO_HEIGHT, type UnitCtx, type UnitVisual, UnitShared, createUnitView } from '../../render/toon/units';
import { lineupX, stageFrame } from './helpers';

export interface HeroStageOptions {
  /** Class ids, left to right (1 for the hero screens, up to 4 for the party / rewards). */
  heroes: readonly string[];
  /** Fraction of the canvas height a hero fills (default 0.72). */
  fill?: number;
  /** Feet position, fraction of the canvas height from the top (default 0.88). */
  feetY?: number;
  /** Horizontal centre of the lineup, fraction of the canvas width (default 0.5). */
  centerX?: number;
  /** Spacing between heroes in world units (default 1.75). */
  gap?: number;
  /** Turn from facing the camera, radians (default 0.3: a slight three-quarter view). */
  turn?: number;
  /** Drag to rotate (default true for one hero, false for a lineup). */
  interactive?: boolean;
  /** Gold ring engraving on the floor (default true). */
  ring?: boolean;
  /** Floor reflection (default true). */
  reflect?: boolean;
  /** Called once every hero is posed and visible. */
  onReady?: () => void;
}

export interface HeroStage {
  /** The stage element (absolute, fills the host). */
  el: HTMLElement;
  /** Resolves when the first set of heroes is visible (rejects never; failures fall back to capsules). */
  ready: Promise<void>;
  setHeroes(heroes: readonly string[]): Promise<void>;
  /** Extra yaw (radians) on top of `turn` (drag writes this too). */
  setYaw(yaw: number): void;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Shared renderer (one WebGL context for every stage)
// ---------------------------------------------------------------------------

let gl: THREE.WebGLRenderer | null = null;
const stack: StageImpl[] = [];

function renderer(): THREE.WebGLRenderer {
  if (gl) return gl;
  gl = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  gl.setClearColor(0x000000, 0);
  gl.toneMapping = THREE.NeutralToneMapping;
  gl.toneMappingExposure = 1.0;
  gl.outputColorSpace = THREE.SRGBColorSpace;
  gl.domElement.className = 'km-stage-canvas';
  gl.domElement.style.display = 'block';
  return gl;
}

/** Free the shared WebGL context. Safe any time; a later stage creates a new one. */
export function releaseHeroStageGL(): void {
  if (!gl || stack.length) return;
  gl.domElement.remove();
  gl.dispose();
  gl.forceContextLoss();
  gl = null;
}

// ---------------------------------------------------------------------------
// Floor art (canvas texture): spotlight pool + gold ring engraving, alpha fades to the edge
// ---------------------------------------------------------------------------

function floorTexture(ring: boolean): THREE.CanvasTexture {
  const N = 1024;
  const c = document.createElement('canvas');
  c.width = c.height = N;
  const g = c.getContext('2d')!;
  const m = N / 2;
  // Base: deep navy, opaque in the middle, transparent at the rim.
  const base = g.createRadialGradient(m, m, 0, m, m, m);
  base.addColorStop(0, 'rgba(22,34,70,0.96)');
  base.addColorStop(0.45, 'rgba(14,24,54,0.9)');
  base.addColorStop(0.75, 'rgba(10,18,42,0.55)');
  base.addColorStop(1, 'rgba(8,14,34,0)');
  g.fillStyle = base;
  g.fillRect(0, 0, N, N);
  // Spotlight pool (soft, cool white-blue, moderate).
  const pool = g.createRadialGradient(m, m, 0, m, m, N * 0.15);
  pool.addColorStop(0, 'rgba(120,160,235,0.42)');
  pool.addColorStop(0.55, 'rgba(80,120,210,0.18)');
  pool.addColorStop(1, 'rgba(60,100,200,0)');
  g.fillStyle = pool;
  g.fillRect(0, 0, N, N);
  if (ring) {
    const R = N * 0.185;
    g.strokeStyle = 'rgba(214,172,96,0.55)';
    g.lineWidth = 2.5;
    g.beginPath();
    g.arc(m, m, R, 0, Math.PI * 2);
    g.stroke();
    g.strokeStyle = 'rgba(214,172,96,0.32)';
    g.lineWidth = 1.5;
    g.beginPath();
    g.arc(m, m, R * 0.93, 0, Math.PI * 2);
    g.stroke();
    g.beginPath();
    g.arc(m, m, R * 1.12, 0, Math.PI * 2);
    g.stroke();
    // Engraved ticks + four small diamonds on the ring.
    g.strokeStyle = 'rgba(214,172,96,0.38)';
    g.lineWidth = 1.5;
    for (let i = 0; i < 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      const r0 = R * (i % 4 === 0 ? 0.95 : 0.975);
      g.beginPath();
      g.moveTo(m + Math.cos(a) * r0, m + Math.sin(a) * r0);
      g.lineTo(m + Math.cos(a) * R * 1.0, m + Math.sin(a) * R * 1.0);
      g.stroke();
    }
    g.fillStyle = 'rgba(232,190,110,0.6)';
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const x = m + Math.cos(a) * R * 1.06;
      const y = m + Math.sin(a) * R * 1.06;
      const d = N * 0.009;
      g.beginPath();
      g.moveTo(x, y - d);
      g.lineTo(x + d, y);
      g.lineTo(x, y + d);
      g.lineTo(x - d, y);
      g.closePath();
      g.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Reflector shader with a radial alpha fade (the mirror dissolves into the page). */
const FADE_REFLECTOR = {
  name: 'KmFadeReflector',
  uniforms: { color: { value: null }, tDiffuse: { value: null }, textureMatrix: { value: null }, uStrength: { value: 0.32 } },
  vertexShader: /* glsl */ `
    uniform mat4 textureMatrix; varying vec4 vUv; varying vec2 vLocal;
    #include <common>
    #include <logdepthbuf_pars_vertex>
    void main() {
      vUv = textureMatrix * vec4(position, 1.0);
      vLocal = uv * 2.0 - 1.0;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      #include <logdepthbuf_vertex>
    }`,
  fragmentShader: /* glsl */ `
    uniform vec3 color; uniform sampler2D tDiffuse; uniform float uStrength; varying vec4 vUv; varying vec2 vLocal;
    #include <logdepthbuf_pars_fragment>
    void main() {
      #include <logdepthbuf_fragment>
      vec4 base = texture2DProj(tDiffuse, vUv);
      float r = length(vLocal);
      float fade = 1.0 - smoothstep(0.15, 0.8, r);
      gl_FragColor = vec4(base.rgb * color, base.a * fade * uStrength);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`,
};

// ---------------------------------------------------------------------------
// Stage
// ---------------------------------------------------------------------------

const FOV = 24;
const PITCH = 0.2;

interface Live {
  view: UnitVisual;
  unit: RenderUnit;
  x: number;
}

function heroUnit(cls: string, i: number): RenderUnit {
  return {
    id: `stage-${cls}-${i}` as unknown as UnitId, name: cls, kind: 'player', faction: 'party' as RenderUnit['faction'], role: null, tier: null,
    pos: { x: 0, y: 0 }, facing: -Math.PI / 2, radius: 0.5, alive: true, hpFrac: 1,
    color: '#fff', isPlayer: true, casting: false, classId: cls, speed: 0, inCombat: false, cast: null,
  };
}

class StageImpl implements HeroStage {
  readonly el: HTMLElement;
  ready: Promise<void>;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);
  private readonly shared = new UnitShared();
  private readonly hulls = { unit: new Set<THREE.Object3D>(), prop: new Set<THREE.Object3D>() };
  private readonly floorTex: THREE.CanvasTexture;
  private readonly floor: THREE.Mesh;
  private reflector: Reflector | null = null;
  private live: Live[] = [];
  private assets: ToonAssets | null = null;
  private ctx: UnitCtx | null = null;
  private raf = 0;
  private last = 0;
  private visible = true;
  private disposed = false;
  private yaw = 0;
  private seq = 0;
  private size = { w: 1, h: 1 };
  private readonly ro: ResizeObserver | null;
  private readonly io: IntersectionObserver | null;
  private readonly onVis = (): void => this.kick();
  private drag: { x: number; yaw: number; id: number } | null = null;

  constructor(private readonly host: HTMLElement, private readonly o: HeroStageOptions) {
    this.el = document.createElement('div');
    this.el.className = 'km-stage loading';
    host.appendChild(this.el);

    // Lights: soft key from the front-top, cool fill, blue rim from behind.
    this.scene.add(new THREE.HemisphereLight(0xc8d6ff, 0x1a2244, 0.55));
    const key = new THREE.DirectionalLight(0xfff4e6, 1.8);
    key.position.set(1.5, 6, 6);
    const rim = new THREE.DirectionalLight(0x5f8cff, 2.0);
    rim.position.set(-2.5, 4, -6);
    const rim2 = new THREE.DirectionalLight(0x7aa8ff, 1.1);
    rim2.position.set(3, 3, -5);
    this.scene.add(key, rim, rim2);

    this.floorTex = floorTexture(o.ring !== false);
    const fm = new THREE.MeshBasicMaterial({ map: this.floorTex, transparent: true, depthWrite: false, toneMapped: false });
    this.floor = new THREE.Mesh(new THREE.CircleGeometry(4.2, 96).rotateX(-Math.PI / 2), fm);
    this.floor.renderOrder = 0;
    this.scene.add(this.floor);
    if (o.reflect !== false) {
      const geo = new THREE.CircleGeometry(3.2, 64);
      const ref = new Reflector(geo, { textureWidth: 512, textureHeight: 512, clipBias: 0.003, color: 0x9aaee0, shader: FADE_REFLECTOR });
      const mat = ref.material as THREE.ShaderMaterial;
      mat.transparent = true;
      mat.depthWrite = false;
      ref.rotateX(-Math.PI / 2);
      ref.position.y = 0.004;
      ref.renderOrder = 1;
      this.reflector = ref;
      this.scene.add(ref);
    }
    this.shared.blobs.renderOrder = 2;
    this.scene.add(this.shared.blobs);

    // Drag to rotate.
    if (o.interactive ?? o.heroes.length <= 1) {
      this.el.classList.add('interactive');
      this.el.addEventListener('pointerdown', this.onDown);
    }

    this.ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.resize()) : null;
    this.ro?.observe(this.el);
    this.io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver((es) => {
        this.visible = es.some((e) => e.isIntersecting);
        this.kick();
      })
      : null;
    this.io?.observe(this.el);
    document.addEventListener('visibilitychange', this.onVis);

    // Take the shared canvas.
    stack.push(this);
    this.attach();
    this.ready = this.setHeroes(o.heroes);
  }

  // --- canvas ownership ----------------------------------------------------

  private get owns(): boolean {
    return stack[stack.length - 1] === this;
  }

  private attach(): void {
    const r = renderer();
    if (r.domElement.parentElement !== this.el) this.el.prepend(r.domElement);
    this.resize();
    this.kick();
  }

  private resize(): void {
    const w = Math.max(1, Math.round(this.el.clientWidth));
    const h = Math.max(1, Math.round(this.el.clientHeight));
    this.size = { w, h };
    if (this.owns && gl) {
      gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      gl.setSize(w, h, false);
      gl.domElement.style.width = '100%';
      gl.domElement.style.height = '100%';
    }
    this.frame();
  }

  /** Recompute the camera for the current size and lineup. */
  private frame(): void {
    const { w, h } = this.size;
    const aspect = w / h;
    const xs = this.live.map((l) => l.x);
    const lineupWidth = xs.length > 1 ? Math.max(...xs) - Math.min(...xs) + 1.2 : 0;
    const f = stageFrame({ aspect, fovDeg: FOV, heroHeight: HERO_HEIGHT, fill: this.o.fill ?? 0.72, feetY: this.o.feetY ?? 0.88, lineupWidth });
    const cam = this.camera;
    cam.aspect = aspect;
    cam.position.set(0, f.targetY + Math.sin(PITCH) * f.dist, Math.cos(PITCH) * f.dist);
    cam.lookAt(0, f.targetY, 0);
    const cx = this.o.centerX ?? 0.5;
    if (Math.abs(cx - 0.5) > 1e-3) cam.setViewOffset(w, h, (0.5 - cx) * w, 0, w, h);
    else cam.clearViewOffset();
    cam.updateProjectionMatrix();
  }

  // --- heroes ----------------------------------------------------------------

  async setHeroes(heroes: readonly string[]): Promise<void> {
    const my = ++this.seq;
    this.el.classList.add('loading');
    if (!this.assets) {
      try {
        this.assets = await loadToonAssets();
      } catch (err) {
        console.warn('[kmmo] hero stage: assets failed to load', err);
        return;
      }
      this.ctx = { assets: this.assets, hulls: this.hulls, shared: this.shared, npcDef: () => undefined, mobShadows: false, springHz: 60 };
    }
    if (this.disposed || my !== this.seq) return;
    const assets = this.assets;
    // Make sure a free VRM instance exists for each hero (parses an extra copy if the game holds one).
    const list = heroes.slice(0, 4);
    for (const cls of list) {
      const file = HERO_VRM[cls];
      if (!file) continue;
      const slot = await assets.acquireVrm(file);
      if (slot) slot.inUse = false;
    }
    if (this.disposed || my !== this.seq) return;
    this.clearHeroes();
    const xs = lineupX(list.length, this.o.gap ?? 1.75);
    list.forEach((cls, i) => {
      const unit = heroUnit(cls, i);
      const view = createUnitView(unit, this.ctx!);
      this.scene.add(view.root);
      this.live.push({ view, unit, x: xs[i]! });
    });
    // Settle the idle pose and spring bones before the first visible frame.
    for (let k = 0; k < 45; k++) this.step(1 / 60);
    this.frame();
    this.render();
    this.el.classList.remove('loading');
    this.kick();
    if (my === 1) this.o.onReady?.();
  }

  private clearHeroes(): void {
    for (const l of this.live) l.view.dispose();
    this.live = [];
  }

  setYaw(yaw: number): void {
    this.yaw = yaw;
    this.kick();
  }

  // --- loop --------------------------------------------------------------------

  private step(dt: number): void {
    const turn = this.o.turn ?? 0.3;
    this.shared.beginBlobs();
    for (const l of this.live) {
      l.unit.facing = -Math.PI / 2 + turn + this.yaw;
      l.view.update(l.unit, dt, 0);
      // Stand on the stage floor (the game's ground height function may describe a dungeon).
      l.view.root.position.set(l.x, 0, 0);
      if (l.view.blobR > 0) this.shared.addBlob(l.x, 0, l.view.blobR * 0.9, 0);
    }
    this.shared.endBlobs();
  }

  private render(): void {
    if (!this.owns || !gl) return;
    gl.render(this.scene, this.camera);
  }

  private get running(): boolean {
    return !this.disposed && this.owns && this.visible && !document.hidden && this.live.length > 0 && this.el.isConnected;
  }

  private kick(): void {
    if (this.raf || !this.running) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    if (!this.running) return;
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    this.step(dt);
    this.render();
    this.raf = requestAnimationFrame(this.tick);
  };

  // --- drag ------------------------------------------------------------------------

  private readonly onDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    this.drag = { x: e.clientX, yaw: this.yaw, id: e.pointerId };
    this.el.setPointerCapture?.(e.pointerId);
    this.el.classList.add('dragging');
    this.el.addEventListener('pointermove', this.onMove);
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onUp);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (!this.drag || e.pointerId !== this.drag.id) return;
    this.yaw = this.drag.yaw + (e.clientX - this.drag.x) * 0.012;
  };

  private readonly onUp = (): void => {
    this.drag = null;
    this.el.classList.remove('dragging');
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
  };

  // --- teardown ----------------------------------------------------------------------

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.seq++;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.onUp();
    this.el.removeEventListener('pointerdown', this.onDown);
    this.ro?.disconnect();
    this.io?.disconnect();
    document.removeEventListener('visibilitychange', this.onVis);
    this.clearHeroes();
    this.shared.dispose();
    this.floor.geometry.dispose();
    (this.floor.material as THREE.Material).dispose();
    this.floorTex.dispose();
    this.reflector?.dispose();
    const wasOwner = this.owns;
    const i = stack.indexOf(this);
    if (i >= 0) stack.splice(i, 1);
    if (gl && gl.domElement.parentElement === this.el) gl.domElement.remove();
    this.el.remove();
    // Hand the canvas back to the previous stage, if any.
    if (wasOwner) stack[stack.length - 1]?.attach();
  }
}

/** Mount a hero stage that fills `host` (give the host a size; the stage is absolutely positioned). */
export function mountHeroStage(host: HTMLElement, o: HeroStageOptions): HeroStage {
  return new StageImpl(host, o);
}
